import type { AdminProjectAccessProject } from '@frontend/types';
import type { ProvisionRunStatus, ProvisionStartResponse } from '@frontend/types/provision';
import { adminListEcosystems } from '@frontend/api-clients/ecosystemsClient';

export interface AdminProjectCatalogEntry {
  id: string;
  name: string;
  ecosystemId: string;
  description?: string;
}

export interface ProjectMovePreview {
  projectId: string;
  sourceEcosystemId: string;
  targetEcosystemId: string;
  plannedSteps: string[];
  cascadedProjectIds?: string[];
}

export interface ProjectCreateScope {
  eligible: boolean;
  parentRequired: boolean;
  parentRequiredGlobally: boolean;
  ecosystems: Array<{
    id: string;
    displayName: string;
    memberGroup: string;
    services: string[];
    packageCatalog: Array<{ id: string; label: string }>;
    availableServices: string[];
    realmServices: string[];
    canAddRealmServices: boolean;
    canAddEcosystemServices: boolean;
  }>;
  parentProjects: Array<{ id: string; ecosystemId: string; name: string }>;
}

export interface StartProjectCreatePayload {
  id: string;
  ecosystemId: string;
  name: string;
  description?: string;
  parentProjectId?: string;
  services?: string[];
  includeRepository?: boolean;
  openprojectUrl?: string;
  nextcloudUrl?: string;
  giteaUrl?: string;
  flags?: { dryRun?: boolean; sync?: boolean; skipWoodpecker?: boolean };
  clientReferenceId?: string;
  projection?: Record<string, unknown>;
}

async function adminOrchFetch<T>(
  method: string,
  path: string,
  token: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = (await res.json().catch(() => ({}))) as T & { message?: string; code?: string };
  if (!res.ok) {
    const err = new Error(data.message || `Request failed: ${res.status}`) as Error & {
      code?: string;
      status?: number;
      correlationId?: string;
    };
    err.code = data.code;
    err.status = res.status;
    const cid = (data as { correlationId?: string }).correlationId;
    if (cid) err.correlationId = cid;
    throw err;
  }
  return data;
}

/** Fan-out project catalog across ecosystems (patriot-admin). */
export async function loadAdminProjectCatalog(token: string): Promise<AdminProjectCatalogEntry[]> {
  const ecosystems = await adminListEcosystems(token);
  const active = ecosystems.filter((e) => !e.decommissioned);
  const rows: AdminProjectCatalogEntry[] = [];

  await Promise.all(
    active.map(async (eco) => {
      const res = await fetch(
        `/api/admin/project-access?ecosystem=${encodeURIComponent(eco.id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = (await res.json()) as { projects?: AdminProjectAccessProject[] };
      for (const p of data.projects || []) {
        rows.push({
          id: p.id,
          name: p.name || p.id,
          ecosystemId: p.ecosystemId || eco.id,
          description: p.description,
        });
      }
    })
  );

  rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return rows;
}

export async function previewProjectMove(
  token: string,
  projectId: string,
  targetEcosystemId: string
): Promise<ProjectMovePreview> {
  const data = await adminOrchFetch<{
    dryRun?: boolean;
    projectId?: string;
    sourceEcosystemId?: string;
    targetEcosystemId?: string;
    plannedSteps?: string[];
    cascadedProjectIds?: string[];
  }>(
    'PATCH',
    `/api/admin/projects/${encodeURIComponent(projectId)}/ecosystem`,
    token,
    { ecosystemId: targetEcosystemId, flags: { dryRun: true } }
  );
  return {
    projectId: data.projectId || projectId,
    sourceEcosystemId: data.sourceEcosystemId || '',
    targetEcosystemId: data.targetEcosystemId || targetEcosystemId,
    plannedSteps: data.plannedSteps || [],
    cascadedProjectIds: data.cascadedProjectIds || [],
  };
}

export async function startProjectMove(
  token: string,
  projectId: string,
  targetEcosystemId: string,
  reason?: string
): Promise<ProvisionStartResponse> {
  return adminOrchFetch<ProvisionStartResponse>(
    'PATCH',
    `/api/admin/projects/${encodeURIComponent(projectId)}/ecosystem`,
    token,
    { ecosystemId: targetEcosystemId, reason: reason?.trim() || undefined }
  );
}

export async function previewProjectCreate(
  token: string,
  payload: StartProjectCreatePayload
): Promise<{ dryRun: boolean; plannedSteps?: string[]; runState?: string; projectId?: string }> {
  return adminOrchFetch('POST', '/api/admin/projects', token, {
    ...payload,
    flags: { ...(payload.flags || {}), dryRun: true, sync: false },
  });
}

export async function startProjectCreateAsync(
  token: string,
  payload: StartProjectCreatePayload,
  opts?: { idempotencyKey?: string }
): Promise<ProvisionStartResponse & { httpStatus?: number }> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const key = opts?.idempotencyKey?.trim();
  if (key) headers['Idempotency-Key'] = key;

  const res = await fetch('/api/admin/projects', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...payload,
      flags: { ...(payload.flags || {}), sync: Boolean(payload.flags?.sync) },
    }),
  });
  const data = (await res.json().catch(() => ({}))) as ProvisionStartResponse & {
    message?: string;
    code?: string;
  };
  if (!res.ok) {
    const err = new Error(data.message || `Request failed: ${res.status}`) as Error & {
      code?: string;
      status?: number;
      correlationId?: string;
    };
    err.code = data.code;
    err.status = res.status;
    if (data.correlationId) err.correlationId = data.correlationId;
    throw err;
  }
  return { ...data, httpStatus: res.status };
}

export async function fetchProjectCreateScope(token: string): Promise<ProjectCreateScope> {
  return adminOrchFetch<ProjectCreateScope>('GET', '/api/admin/projects/create-scope', token);
}

export async function getProjectProvisionStatus(
  token: string,
  correlationId: string
): Promise<ProvisionRunStatus> {
  return adminOrchFetch<ProvisionRunStatus>(
    'GET',
    `/api/admin/projects/provision/status/${encodeURIComponent(correlationId)}`,
    token
  );
}

export function isProvisionTerminal(runState: string): boolean {
  return runState === 'completed' || runState === 'partial' || runState === 'failed';
}
